/**
 * MyQuotesPage - Dashboard for users to track their quote requests and responses
 * Shows submitted quote requests and received quotes from providers
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileQuestion, Clock, CheckCircle, XCircle, MessageSquare,
  Calendar, MapPin, User, Mail, Phone, ChevronRight, 
  ArrowLeft, Loader2, RefreshCw, Plus, Filter
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_COLORS = {
  PENDING: 'bg-amber-100 text-amber-700 border-amber-200',
  QUOTED: 'bg-blue-100 text-blue-700 border-blue-200',
  ACCEPTED: 'bg-green-100 text-green-700 border-green-200',
  REJECTED: 'bg-red-100 text-red-700 border-red-200',
  EXPIRED: 'bg-gray-100 text-gray-500 border-gray-200',
};

// Quote type icons and names
const QUOTE_TYPES = {
  repair: { icon: '🔧', name: 'Instrument Repair' },
  setup: { icon: '⚙️', name: 'Professional Setup' },
  custom_build: { icon: '🎸', name: 'Custom Build' },
  restoration: { icon: '🪄', name: 'Restoration' },
  moving: { icon: '🚚', name: 'Instrument Moving' },
};

// Quote Request Card
function QuoteRequestCard({ quote, onViewDetails }) {
  const type = QUOTE_TYPES[quote.quote_type] || { icon: '📋', name: quote.quote_type };
  const statusClass = STATUS_COLORS[quote.status] || STATUS_COLORS.PENDING;
  
  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => onViewDetails(quote)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-2xl">
              {type.icon}
            </div>
            <div>
              <h3 className="font-semibold">{type.name}</h3>
              <p className="text-sm text-gray-500">{quote.instrument_type}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                <MapPin className="w-3 h-3" />
                <span>{quote.city}</span>
                <span>•</span>
                <Calendar className="w-3 h-3" />
                <span>{new Date(quote.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
          
          <div className="text-right">
            <Badge className={`${statusClass} border`}>
              {quote.status}
            </Badge>
            {quote.quotes_received > 0 && (
              <p className="text-sm text-blue-600 mt-2">
                {quote.quotes_received} quote{quote.quotes_received !== 1 ? 's' : ''} received
              </p>
            )}
          </div>
        </div>
        
        {/* Description Preview */}
        <p className="mt-3 text-sm text-gray-600 line-clamp-2">
          {quote.issue_description}
        </p>
        
        {quote.brand && (
          <div className="mt-2 flex items-center gap-2">
            <Badge variant="outline" className="text-xs">{quote.brand}</Badge>
            {quote.model && <Badge variant="outline" className="text-xs">{quote.model}</Badge>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Quote Response Card
function QuoteResponseCard({ response, onAccept, onReject }) {
  return (
    <Card className="border-l-4 border-l-blue-500">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-semibold">{response.provider_name}</h4>
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              <MapPin className="w-3 h-3" />
              <span>{response.provider_location}</span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-green-600">
              {response.currency_symbol}{response.quote_amount.toLocaleString()}
            </p>
            <p className="text-xs text-gray-400">
              Received {new Date(response.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        {response.message && (
          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">{response.message}</p>
          </div>
        )}
        
        {response.estimated_completion && (
          <p className="mt-2 text-sm text-gray-500">
            <Clock className="w-3 h-3 inline mr-1" />
            Est. completion: {response.estimated_completion}
          </p>
        )}
        
        <div className="flex gap-2 mt-4">
          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => onAccept(response)}>
            <CheckCircle className="w-4 h-4 mr-1" />
            Accept Quote
          </Button>
          <Button size="sm" variant="outline" onClick={() => onReject(response)}>
            <XCircle className="w-4 h-4 mr-1" />
            Decline
          </Button>
          <Button size="sm" variant="ghost">
            <MessageSquare className="w-4 h-4 mr-1" />
            Message
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Quote Detail Modal/Panel
function QuoteDetailPanel({ quote, onClose, onRefresh }) {
  const [responses, setResponses] = useState([]);
  const [loadingResponses, setLoadingResponses] = useState(true);
  
  const type = QUOTE_TYPES[quote.quote_type] || { icon: '📋', name: quote.quote_type };
  
  useEffect(() => {
    // In a real app, fetch quote responses from API
    // For now, simulate with empty array
    setLoadingResponses(true);
    setTimeout(() => {
      setResponses([]);
      setLoadingResponses(false);
    }, 500);
  }, [quote.quote_id]);
  
  const handleAcceptQuote = async (response) => {
    toast.success('Quote accepted! Provider will be notified.');
  };
  
  const handleRejectQuote = async (response) => {
    toast.info('Quote declined');
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{type.icon}</span>
              <div>
                <CardTitle>{type.name}</CardTitle>
                <CardDescription>Quote ID: {quote.quote_id}</CardDescription>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <XCircle className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Request Details */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Instrument</p>
                <p className="font-medium">{quote.instrument_type}</p>
              </div>
              {quote.brand && (
                <div>
                  <p className="text-sm text-gray-500">Brand / Model</p>
                  <p className="font-medium">{quote.brand} {quote.model || ''}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Location</p>
                <p className="font-medium">{quote.city}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Urgency</p>
                <Badge variant="outline">
                  {quote.urgency === 'emergency' ? '🚨 Emergency' : 
                   quote.urgency === 'priority' ? '⚡ Priority' : '📅 Standard'}
                </Badge>
              </div>
            </div>
            
            <div>
              <p className="text-sm text-gray-500 mb-1">Description</p>
              <p className="p-3 bg-gray-50 rounded-lg text-sm">{quote.issue_description}</p>
            </div>
            
            {quote.budget_range && (
              <div>
                <p className="text-sm text-gray-500">Budget Range</p>
                <p className="font-medium">{quote.budget_range}</p>
              </div>
            )}
            
            {/* Contact Details */}
            <div className="border-t pt-4">
              <p className="text-sm text-gray-500 mb-2">Your Contact Details</p>
              <div className="flex flex-wrap gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4 text-gray-400" />
                  {quote.customer?.name}
                </span>
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4 text-gray-400" />
                  {quote.customer?.email}
                </span>
                {quote.customer?.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4 text-gray-400" />
                    {quote.customer.phone}
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Received Quotes */}
          <div className="mt-6 border-t pt-6">
            <h3 className="font-semibold mb-4">
              Received Quotes ({quote.quotes_received || 0})
            </h3>
            
            {loadingResponses ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : responses.length > 0 ? (
              <div className="space-y-4">
                {responses.map((response, idx) => (
                  <QuoteResponseCard 
                    key={idx}
                    response={response}
                    onAccept={handleAcceptQuote}
                    onReject={handleRejectQuote}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No quotes received yet</p>
                <p className="text-sm">Providers in your area will respond within 24-48 hours</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Component
export default function MyQuotesPage() {
  const navigate = useNavigate();
  const { country } = useCountry();
  
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedQuote, setSelectedQuote] = useState(null);
  
  // Fetch user's quote requests
  const fetchQuotes = async () => {
    setLoading(true);
    try {
      // In a real app, this would filter by user
      const response = await fetch(`${API_URL}/api/music/quote-requests?country=${country}`);
      const data = await response.json();
      
      if (data.success) {
        setQuotes(data.quotes || []);
      }
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      toast.error('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchQuotes();
  }, [country]);
  
  // Filter quotes by status
  const filteredQuotes = quotes.filter(quote => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return quote.status === 'PENDING';
    if (activeTab === 'quoted') return quote.quotes_received > 0;
    if (activeTab === 'completed') return ['ACCEPTED', 'REJECTED'].includes(quote.status);
    return true;
  });
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white py-6 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/10"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <FileQuestion className="w-7 h-7" />
                  My Quote Requests
                </h1>
                <p className="text-orange-100">Track your requests and received quotes</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                onClick={fetchQuotes}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button 
                className="bg-white text-orange-600 hover:bg-orange-50"
                onClick={() => navigate('/music/quote')}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Request
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white shadow-sm mb-6">
            <TabsTrigger value="all" className="flex items-center gap-1">
              All
              <Badge variant="secondary" className="ml-1">{quotes.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="pending" className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              Pending
            </TabsTrigger>
            <TabsTrigger value="quoted" className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              Quoted
            </TabsTrigger>
            <TabsTrigger value="completed" className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              Completed
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab}>
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
              </div>
            ) : filteredQuotes.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {filteredQuotes.map((quote) => (
                  <QuoteRequestCard 
                    key={quote.quote_id}
                    quote={quote}
                    onViewDetails={setSelectedQuote}
                  />
                ))}
              </div>
            ) : (
              <Card className="text-center py-16">
                <CardContent>
                  <FileQuestion className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold mb-2">No Quote Requests</h3>
                  <p className="text-gray-500 mb-6">
                    {activeTab === 'all' 
                      ? "You haven't submitted any quote requests yet."
                      : `No ${activeTab} quote requests found.`}
                  </p>
                  <Button onClick={() => navigate('/music/quote')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Request Your First Quote
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
        
        {/* Info Box */}
        <Card className="mt-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-800">How It Works</h4>
              <ul className="text-sm text-blue-700 mt-1 space-y-1">
                <li>1. Submit a quote request with your requirements</li>
                <li>2. Local providers receive your request and send quotes</li>
                <li>3. Compare quotes and choose the best offer</li>
                <li>4. Connect directly with your chosen provider</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Quote Detail Panel */}
      {selectedQuote && (
        <QuoteDetailPanel 
          quote={selectedQuote}
          onClose={() => setSelectedQuote(null)}
          onRefresh={fetchQuotes}
        />
      )}
    </div>
  );
}
